"use client";

import { useEffect, useId, useState } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceArea,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { ExternalLink, Search, Swords, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatCompactCurrency, formatPercent, formatRatio } from "@/lib/format";
import { InstitutionalPanel } from "@/components/peer-group/institutional-panel";
import { PeerKpiComparison } from "@/components/peer-kpi-comparison";
import { PEER_GROUP } from "@/lib/config";
import { RangeSelector, type ChartRangeLabel } from "@/components/charts/range-selector";
import {
  computeNiceYAxisTicks,
  computePriceAxisTicks,
  filterDateRange,
  formatPriceAxisTick,
  indexTo100,
  PEER_COLORS,
  withTimeField,
} from "@/lib/price-chart-utils";
import { useIsNarrowViewport } from "@/lib/use-narrow-viewport";
import { useDragZoom } from "@/lib/use-drag-zoom";
import type { ScoreHistoryPoint } from "@/lib/score-history/query";
import type { Fundamentals, HistoricalSeries, PricePoint, SecurityRef } from "@/lib/data/types";

/** "Top 8" laut Institutionelle-Peer-Group-Engine-Spec (lib/config.ts -> PEER_GROUP),
 * damit die institutionelle Auswertung unterhalb genug Kandidaten hat, aus denen sie
 * die tatsaechlich passende Gruppe (Marktkapitalisierungs-Klammer) herausfiltern kann. */
const MAX_PEERS = PEER_GROUP.maxPeers;

// Indexierte Vergleichs-Kurscharts sind wie price-history-chart.tsx tagesgranular.
const MIN_SPAN_MS = 3 * 24 * 60 * 60 * 1000; // 3 Tage

interface PeerData {
  symbol: string;
  name: string;
  currency?: string;
  marketCap?: number;
  industry?: string;
  fundamentals: Fundamentals;
  priceHistory: PricePoint[];
  historical: HistoricalSeries;
  scoreHistory?: ScoreHistoryPoint[];
}

type RowFormat = "ratio" | "percent" | "currency";
type GoodDirection = "higher" | "lower";

interface CompareRow {
  label: string;
  format: RowFormat;
  goodDirection: GoodDirection;
  values: (number | undefined)[];
}

interface ChartRow {
  date: string;
  [seriesKey: string]: string | number | undefined;
}

function formatRow(format: RowFormat, value: number | undefined, currency: string | undefined) {
  if (format === "percent") return formatPercent(value);
  if (format === "currency") return formatCompactCurrency(value, currency);
  return formatRatio(value);
}

/** Ermittelt den Index des staerksten Werts einer Vergleichszeile ueber beliebig viele
 * Spalten ("Boxkampf"-Hervorhebung). Liefert null bei weniger als 2 vorhandenen Werten
 * oder einem Gleichstand um den besten Wert - dann keine Hervorhebung. */
function bestIndex(values: (number | undefined)[], direction: GoodDirection): number | null {
  const defined = values
    .map((v, i) => ({ v, i }))
    .filter((x): x is { v: number; i: number } => x.v !== undefined);
  if (defined.length < 2) return null;
  const sorted = [...defined].sort((a, b) => (direction === "higher" ? b.v - a.v : a.v - b.v));
  if (sorted[1].v === sorted[0].v) return null;
  return sorted[0].i;
}

export function PeerCompare({
  symbol,
  name,
  currency,
  marketCap,
  fundamentals,
  priceHistory,
  historical,
  autoSuggestedPeers = [],
  ownScoreHistory = [],
}: {
  symbol: string;
  name: string;
  currency?: string;
  marketCap?: number;
  fundamentals: Fundamentals;
  priceHistory: PricePoint[];
  historical: HistoricalSeries;
  /** "Aktien-Duell": automatisch vorgeschlagene Vergleichsaktien aus demselben Sektor +
   * derselben industry, marktkap-nah (siehe lib/peer-group/auto-suggest.ts). Ersetzt die
   * manuelle Peer-Suche nicht, sondern ergaenzt sie um einen 1-Klick-Einstieg. */
  autoSuggestedPeers?: { symbol: string; name: string; logoUrl?: string }[];
  /** Score-Verlauf der eigenen Aktie (backlog.md -> "Score-Verlauf zweier
   * selbstgewählter Aktien im selben Chart vergleichbar machen") - Peers bringen ihren
   * eigenen scoreHistory bereits über /api/peer mit (siehe lib/data/peer-data.ts), die
   * eigene Aktie braucht dafür diesen zusätzlichen Server-Prop. */
  ownScoreHistory?: ScoreHistoryPoint[];
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<SecurityRef[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  // Tastatur-Bedienbarkeit (Barrierefreiheits-Audit, 2026-08-10) - gleiches Muster wie
  // search-hero.tsx/ticker-combobox.tsx: vorher liess sich ein Vorschlag nur per Maus
  // (onMouseDown) auswaehlen, obwohl die "Aktien-Duell"-Vorschlagsbuttons daneben
  // bereits ganz normale, tastaturbedienbare <button>s sind.
  const [activeIndex, setActiveIndex] = useState(-1);
  const [peers, setPeers] = useState<PeerData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<ChartRangeLabel>("Max");
  const isNarrow = useIsNarrowViewport();
  const peerListboxId = useId();

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      // Kein setSuggestions([]) hier - die Dropdown-Anzeige blendet bei kurzer Query
      // ohnehin aus (siehe showDropdown weiter unten), alte Vorschlaege bleiben nur
      // intern im State, ohne sichtbar zu werden.
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(trimmed)}`);
        if (!res.ok) return;
        const data = (await res.json()) as SecurityRef[];
        setSuggestions(
          data.filter((s) => s.symbol !== symbol && !peers.some((p) => p.symbol === s.symbol)),
        );
        setActiveIndex(-1);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, symbol, peers]);

  async function addPeer(peerSymbol: string) {
    setIsOpen(false);
    setQuery("");
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/peer?symbol=${encodeURIComponent(peerSymbol)}`);
      if (!res.ok) {
        setError("Aktie nicht gefunden oder Daten derzeit nicht abrufbar.");
        return;
      }
      const data = (await res.json()) as PeerData;
      setPeers((prev) => [...prev, data]);
    } catch {
      setError("Aktie nicht gefunden oder Daten derzeit nicht abrufbar.");
    } finally {
      setLoading(false);
    }
  }

  function removePeer(peerSymbol: string) {
    setPeers((prev) => prev.filter((p) => p.symbol !== peerSymbol));
  }

  function handleSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!isOpen || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const chosen = suggestions[activeIndex] ?? suggestions[0];
      addPeer(chosen.symbol);
    } else if (event.key === "Escape") {
      setIsOpen(false);
    }
  }

  // Nur noch die Kennzahlen OHNE echte Jahres-Zeitreihe (backlog.md [Punkt 72]) - KGV/KBV/
  // KUV/Dividendenrendite/Bruttomarge/Nettomarge/ROIC/ROE sind jetzt ueber das
  // Mehrfachauswahl-Dropdown bei "Kennzahlen im Zeitverlauf vergleichen" (PeerKpiComparison
  // unten) abgedeckt - dort sogar mit echtem Verlauf statt nur einer Momentaufnahme. Diese
  // vier haben in lib/data/types.ts keine HistoricalSeries-Entsprechung, bleiben also
  // bewusst als eigene, kleinere Tabelle bestehen statt komplett zu verschwinden.
  const rows: CompareRow[] = [
    {
      label: "Marktkapitalisierung",
      format: "currency",
      goodDirection: "higher",
      values: [marketCap, ...peers.map((p) => p.marketCap)],
    },
    {
      label: "EV/EBITDA",
      format: "ratio",
      goodDirection: "lower",
      values: [fundamentals.evToEbitda, ...peers.map((p) => p.fundamentals.evToEbitda)],
    },
    {
      label: "ROA",
      format: "percent",
      goodDirection: "higher",
      values: [fundamentals.roa, ...peers.map((p) => p.fundamentals.roa)],
    },
    {
      label: "Schuldenquote (D/E)",
      format: "percent",
      goodDirection: "lower",
      values: [fundamentals.debtToEquity, ...peers.map((p) => p.fundamentals.debtToEquity)],
    },
  ];

  // Alle Serien (eigene Aktie + Peers) mit vorhandener Kurshistorie auf den gewaehlten
  // Zeitraum UND den gemeinsam abgedeckten Kalenderzeitraum begrenzen und jeweils auf 100
  // indexieren, statt absolute Kurse mit unterschiedlichem Niveau (z.B. 30 EUR vs.
  // 3.000 EUR) nebeneinander zu plotten.
  const activeSeries = [
    { key: "own", label: symbol, color: "#16265C", priceHistory: filterDateRange(priceHistory, range) },
    ...peers.map((p, i) => ({
      key: p.symbol,
      label: p.symbol,
      color: PEER_COLORS[i],
      priceHistory: filterDateRange(p.priceHistory, range),
    })),
  ].filter((s) => s.priceHistory.length > 0);

  let chartData: (ChartRow & { time: number })[] = [];
  if (activeSeries.length > 1) {
    const start = activeSeries.map((s) => s.priceHistory[0].date).reduce((a, b) => (a > b ? a : b));
    const end = activeSeries
      .map((s) => s.priceHistory[s.priceHistory.length - 1].date)
      .reduce((a, b) => (a < b ? a : b));

    const indexedMaps = activeSeries.map((s) =>
      indexTo100(s.priceHistory.filter((p) => p.date >= start && p.date <= end)),
    );
    const allDates = [...new Set(indexedMaps.flatMap((m) => [...m.keys()]))].sort();
    chartData = withTimeField(
      allDates.map((date) => {
        const entry: ChartRow = { date };
        activeSeries.forEach((s, i) => {
          entry[s.key] = indexedMaps[i].get(date);
        });
        return entry;
      }),
    );
  }
  // Klick+Ziehen zoomt zusaetzlich zum 1J/3J/5J/10J/Max-Range-Selector in einen frei
  // waehlbaren Ausschnitt (Erik 2026-07-11, Feedback-Runde 8 Punkt 4 - fehlte hier noch,
  // siehe STATUS.md "Punkt 4, Teil 2/2"), gleiches Muster wie die uebrigen Charts. Mausrad-/
  // Tastatur-Zoom (neu, Siebzehnte autonome Charge) ueber denselben gemeinsamen Hook -
  // siehe lib/use-drag-zoom.ts.
  const {
    zoomRange,
    resetZoom,
    refAreaLeft,
    refAreaRight,
    chartWrapperRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onKeyDown,
    keyboardA11yProps,
  } = useDragZoom({
    fullMin: chartData[0]?.time,
    fullMax: chartData[chartData.length - 1]?.time,
    minSpan: MIN_SPAN_MS,
  });
  const visibleChartData = zoomRange
    ? chartData.filter((d) => d.time >= zoomRange[0] && d.time <= zoomRange[1])
    : chartData;

  const axisTicks = computePriceAxisTicks(visibleChartData, range, isNarrow ? 5 : 8).map((d) => Date.parse(d));
  // Ersetzt Rechartsʼ Auto-Tick-Heuristik, die den obersten Tick teils nicht auf den
  // gleichen Rhythmus wie die uebrigen Ticks rundete (backlog.md -> Runde 5 Punkt 19).
  const yAxisTicks = computeNiceYAxisTicks(
    visibleChartData.flatMap((row) => activeSeries.map((s) => row[s.key]).filter((v): v is number => typeof v === "number")),
    false,
  );

  // Fuer den KPI-Zeitverlaufs-Vergleich (Feedback-Runde 8 Punkt 6) - volle Firmennamen
  // statt Ticker (konsistent mit dem Duell-Vorschlag-Fix), dieselben Farben wie im
  // Preis-Index-Chart oben.
  const kpiSeries = [
    { key: "own", label: name, color: "#16265C", historical },
    ...peers.map((p, i) => ({ key: p.symbol, label: p.name, color: PEER_COLORS[i], historical: p.historical })),
  ];

  // Score-Verlauf-Vergleich (backlog.md -> "Score-Verlauf zweier selbstgewählter Aktien im
  // selben Chart vergleichbar machen") - anders als der Preis-Chart oben KEINE Index-auf-
  // 100-Normalisierung nötig, der Erikonomie-Score ist bereits eine gemeinsame 0-100-Skala.
  // Kein eigener Zoom/Zeitraum-Selector (anders als der Preis-Chart) - score_history wird
  // erst seit Kurzem taeglich erfasst, die Reihe ist dafuer noch zu kurz.
  const scoreSeries = [
    { key: "own", label: symbol, color: "#16265C", scoreHistory: ownScoreHistory },
    ...peers.map((p, i) => ({
      key: p.symbol,
      label: p.symbol,
      color: PEER_COLORS[i],
      scoreHistory: p.scoreHistory ?? [],
    })),
  ].filter((s) => s.scoreHistory.length > 0);

  const scoreChartData: (ChartRow & { time: number })[] =
    scoreSeries.length > 1
      ? withTimeField(
          [...new Set(scoreSeries.flatMap((s) => s.scoreHistory.map((h) => h.capturedAt)))]
            .sort()
            .map((date) => {
              const entry: ChartRow = { date };
              scoreSeries.forEach((s) => {
                entry[s.key] = s.scoreHistory.find((h) => h.capturedAt === date)?.score;
              });
              return entry;
            }),
        )
      : [];

  const visibleSuggestions = autoSuggestedPeers.filter(
    (s) => !peers.some((p) => p.symbol === s.symbol),
  );

  return (
    <div className="flex flex-col gap-6">
      {peers.length < MAX_PEERS && visibleSuggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Swords className="h-3.5 w-3.5" aria-hidden="true" />
            Dein Duell-Vorschlag:
          </span>
          {/* Klick auf den Vorschlag selbst setzt ihn direkt als Vergleich (backlog.md ->
              [Punkt 67+70], Erik-Bugreport: "Klick auf eine vorgeschlagene Aktie" führte
              bisher zur Profilseite statt zum Vergleich - nur die manuelle Suche
              funktionierte wie erwartet). Der bisherige separate Profil-Link (Feedback-Runde
              16 Punkt 10, 2026-07-22) bleibt als kleines, klar abgesetztes Icon erhalten,
              ist jetzt aber die sekundäre statt der primären Aktion. */}
          {visibleSuggestions.map((s) => (
            <div
              key={s.symbol}
              className="flex items-center gap-0.5 rounded-full border border-hairline bg-card py-1 pr-1 pl-2.5 text-xs text-ink-secondary transition-colors hover:border-gold"
            >
              <button
                type="button"
                onClick={() => addPeer(s.symbol)}
                disabled={loading}
                aria-label={`${s.name} zum Vergleich hinzufügen`}
                title="Zum Vergleich hinzufügen"
                className="flex items-center gap-1.5 hover:text-ink disabled:opacity-50"
              >
                {s.logoUrl && (
                  <Image
                    src={s.logoUrl}
                    alt=""
                    width={16}
                    height={16}
                    unoptimized
                    className="h-4 w-4 shrink-0 rounded-full object-contain"
                  />
                )}
                <span className="font-medium text-ink">{s.name}</span>
                <span className="text-muted-foreground">vs. {symbol}</span>
              </button>
              <Link
                href={`/${s.symbol}`}
                aria-label={`Profil von ${s.name} ansehen`}
                title="Profil ansehen"
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-gold/15 hover:text-ink"
              >
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          ))}
        </div>
      )}
      {peers.length < MAX_PEERS && (
        <Popover
          open={isOpen && query.trim().length >= 2 && suggestions.length > 0}
          onOpenChange={(open) => setIsOpen(open)}
        >
          <PopoverAnchor asChild>
            {/* Praesenter statt der bisherigen h-9/max-w-sm-Miniaturgroesse (Feedback-Runde
                16 Punkt 10, 2026-07-22: "Suchfeld aktuell klein") - gleiche
                Gold-Fokus-Konvention wie SearchHero (components/search/search-hero.tsx),
                spuerbar groesser als die kompakte Variante dort, aber bewusst kleiner als
                deren volle Hero-Groesse (h-14), da dies hier ein Tab-Inhalt und keine
                Landingpage-Hero-Suche ist. */}
            <div className="relative max-w-md">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleSearchKeyDown}
                placeholder={
                  peers.length === 0 ? "Vergleichsaktie suchen …" : "Weitere Vergleichsaktie suchen …"
                }
                disabled={loading}
                role="combobox"
                aria-expanded={isOpen && query.trim().length >= 2 && suggestions.length > 0}
                aria-controls={peerListboxId}
                aria-autocomplete="list"
                className="h-11 rounded-full border-border pl-10 pr-3 text-sm shadow-sm focus-visible:ring-gold-text"
              />
            </div>
          </PopoverAnchor>
          {/* Popover statt einer einfachen absolut positionierten Liste: die umgebende
              Card hat overflow-hidden, das eine gewoehnliche Dropdown-Liste abschneiden
              wuerde. Radix rendert PopoverContent in einem Portal, umgeht das Clipping
              und schliesst bei Aussenklick zuverlaessig von selbst. */}
          <PopoverContent
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            className="w-(--radix-popper-anchor-width) overflow-hidden rounded-2xl border border-border bg-card p-0 shadow-md"
          >
            <ul id={peerListboxId} role="listbox">
              {suggestions.map((s, i) => (
                <li
                  key={`${s.symbol}-${i}`}
                  role="option"
                  aria-selected={i === activeIndex}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addPeer(s.symbol);
                  }}
                  className={cn(
                    "cursor-pointer px-4 py-2.5 text-sm",
                    i === activeIndex ? "bg-muted" : "hover:bg-muted",
                  )}
                >
                  <span className="font-medium text-ink">{s.name}</span>
                  <span className="ml-2 text-muted-foreground">{s.symbol}</span>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      )}
      {peers.length >= MAX_PEERS && (
        <p className="text-xs text-muted-foreground">
          Maximal {MAX_PEERS + 1} Aktien im Vergleich. Entferne eine, um eine andere hinzuzufügen.
        </p>
      )}

      {loading && <p className="text-sm text-muted-foreground">Lade Vergleichsdaten …</p>}
      {error && <p className="text-sm text-negative">{error}</p>}

      {peers.length > 0 && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-ink-secondary" title={[name, ...peers.map((p) => p.name)].join(" vs. ")}>
              {[symbol, ...peers.map((p) => p.symbol)].join(" vs. ")}
            </h3>
            <div className="flex items-center gap-3">
              {zoomRange && (
                <button
                  type="button"
                  onClick={resetZoom}
                  className="text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
                >
                  Zoom zurücksetzen
                </button>
              )}
              <RangeSelector
                value={range}
                onChange={(r) => {
                  setRange(r);
                  resetZoom();
                }}
              />
            </div>
          </div>

          {chartData.length > 1 && (
            <div
              ref={chartWrapperRef}
              className="h-64 w-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              onKeyDown={onKeyDown}
              {...keyboardA11yProps}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={visibleChartData}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  onMouseDown={onMouseDown}
                  onMouseMove={onMouseMove}
                  onMouseUp={onMouseUp}
                >
                  <CartesianGrid stroke="#16265C" strokeOpacity={0.12} strokeDasharray="2 4" />
                  <XAxis
                    dataKey="time"
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    scale="time"
                    ticks={axisTicks}
                    interval={0}
                    tickFormatter={(v: number) => formatPriceAxisTick(v, range)}
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                    axisLine={{ stroke: "#ECEAE3" }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]}
                    ticks={yAxisTicks}
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                    tickFormatter={(v: number) => `${v.toFixed(0)}`}
                    width={48}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(value, name) => [`${Number(value).toFixed(1)}`, name]}
                    labelFormatter={(label) => new Date(Number(label)).toLocaleDateString("de-DE")}
                    contentStyle={{ borderRadius: 4, borderColor: "#ECEAE3", fontSize: 13 }}
                  />
                  <Legend
                    formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
                  />
                  {activeSeries.map((s) => (
                    <Line
                      key={s.key}
                      type="linear"
                      dataKey={s.key}
                      name={s.label}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                  {refAreaLeft !== null && refAreaRight !== null && (
                    <ReferenceArea x1={refAreaLeft} x2={refAreaRight} fill="#16265C" fillOpacity={0.12} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Kurse indexiert auf 100 am Beginn des gemeinsamen Zeitraums, damit Aktien mit
            unterschiedlichem Kursniveau vergleichbar sind.
          </p>

          {scoreChartData.length > 1 && (
            <div className="border-t border-hairline pt-6">
              <h3 className="mb-3 text-sm font-medium text-ink-secondary">Score-Verlauf im Vergleich</h3>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={scoreChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#16265C" strokeOpacity={0.12} strokeDasharray="2 4" />
                    <XAxis
                      dataKey="time"
                      type="number"
                      domain={["dataMin", "dataMax"]}
                      scale="time"
                      tickFormatter={(v: number) => new Date(v).toLocaleDateString("de-DE", { day: "2-digit", month: "short" })}
                      tick={{ fontSize: 12, fill: "#6B7280" }}
                      axisLine={{ stroke: "#ECEAE3" }}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, 100]}
                      ticks={[0, 25, 50, 75, 100]}
                      tick={{ fontSize: 12, fill: "#6B7280" }}
                      width={32}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value, name) => [`${Number(value).toFixed(0)} / 100`, name]}
                      labelFormatter={(label) => new Date(Number(label)).toLocaleDateString("de-DE")}
                      contentStyle={{ borderRadius: 4, borderColor: "#ECEAE3", fontSize: 13 }}
                    />
                    <Legend formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>} />
                    {scoreSeries.map((s) => (
                      <Line
                        key={s.key}
                        type="linear"
                        dataKey={s.key}
                        name={s.label}
                        stroke={s.color}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls
                      />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Täglich erfasster Erikonomie-Score (0-100) - je nach Aktie unterschiedlich
                lange Historie verfügbar.
              </p>
            </div>
          )}

          <div className="border-t border-hairline pt-6">
            <PeerKpiComparison series={kpiSeries} />
          </div>

          <div className="overflow-x-auto border-t border-hairline pt-6">
            <h3 className="text-sm font-medium text-ink-secondary">Aktuelle Kennzahlen</h3>
            <p className="mb-3 text-xs text-muted-foreground">
              Ohne eigene Zeitreihe - Kennzahlen mit Verlauf (Marge, KGV, ROIC, ...) über
              „Kennzahlen wählen“ oben.
            </p>
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b border-hairline text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-4 font-normal">Kennzahl</th>
                  <th className="py-2 pr-4 text-right font-normal">{symbol}</th>
                  {peers.map((p) => (
                    <th key={p.symbol} className="py-2 pr-4 text-right font-normal">
                      <span className="inline-flex items-center gap-1.5">
                        {p.symbol}
                        {/* [Punkt 98] Touch-Zielflaeche vergroessert (12px-Icon ohne
                            Padding), kompensierendes negatives Margin haelt die
                            Kopfzeilen-Ausrichtung unveraendert. */}
                        <button
                          type="button"
                          onClick={() => removePeer(p.symbol)}
                          aria-label={`${p.symbol} aus Vergleich entfernen`}
                          className="-m-1.5 p-1.5 text-muted-foreground hover:text-ink"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows
                  .filter((row) => row.values.some((v) => v !== undefined))
                  .map((row) => {
                    const winner = bestIndex(row.values, row.goodDirection);
                    return (
                      <tr key={row.label} className="border-b border-hairline last:border-0">
                        <td className="py-2.5 pr-4 text-ink-secondary">{row.label}</td>
                        {row.values.map((value, i) => (
                          <td key={i} className="py-2.5 pr-4 text-right font-tabular">
                            <span
                              className={
                                winner === i
                                  ? "rounded-md bg-positive/10 px-2 py-0.5 font-semibold text-positive"
                                  : "text-ink"
                              }
                            >
                              {formatRow(row.format, value, i === 0 ? currency : peers[i - 1]?.currency)}
                            </span>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <InstitutionalPanel
            own={{ symbol, name, fundamentals, historical, marketCap }}
            peers={peers.map((p) => ({
              symbol: p.symbol,
              name: p.name,
              fundamentals: p.fundamentals,
              historical: p.historical,
              marketCap: p.marketCap,
            }))}
          />
        </>
      )}
    </div>
  );
}
